import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { ChatAttachmentService } from '../../../../../services/chat-attachment.service';
import { ChatStateService } from '../../../../../services/chat-state.service';
import { PageRegionCropError, PageRegionCropService } from '../../../../../services/page-region-crop.service';
import {
  NormalizedPoint,
  PageRegionAttachment,
  PageRegionShapeName,
  PageRegionSource,
} from '../../../../../models/chat.models';
import {
  boundsOf,
  PageRotation,
  polygonToDisplay,
  polygonToPage,
} from '../../../../../shared/page-region/region-geometry';

/** The page the selection belongs to. A selection never outlives a change of page. */
export interface RegionChatPageContext {
  projectId: string;
  fileId: string;
  fileName: string;
  pageNumber: number;
  rotation: PageRotation;
  /** Unrotated page size in PDF points, when known. */
  pageSize: { width: number; height: number } | null;
  /** Sheet identifier and title, e.g. "CE-4 · Parking Layout Plan"; stored as the crop description. */
  sheetLabel?: string;
}

/** Where the selection is in its life. */
export type RegionChatPhase = 'idle' | 'selected';

/** Where the crop preview is in its life. */
export type RegionCropStatus = 'none' | 'generating' | 'ready' | 'outdated' | 'error';

/** A region reopened from a chat source link. Shown read-only. */
export interface RestoredRegion {
  attachmentId: string;
  source: PageRegionSource;
  stale: boolean;
  message?: string;
}

/** Resolution below which fine print may be unreadable. */
export const LOW_RESOLUTION_DPI = 100;

/** True for four corners whose sides run along the page axes, in either winding order. */
export function isAxisAlignedRectangle(outline: readonly NormalizedPoint[]): boolean {
  if (outline.length !== 4) return false;
  const near = (a: number, b: number): boolean => Math.abs(a - b) < 1e-9;
  let horizontal = 0;
  let vertical = 0;
  for (let index = 0; index < 4; index++) {
    const from = outline[index];
    const to = outline[(index + 1) % 4];
    if (near(from.y, to.y) && !near(from.x, to.x)) horizontal++;
    else if (near(from.x, to.x) && !near(from.y, to.y)) vertical++;
    else return false;
  }
  return horizontal === 2 && vertical === 2;
}

/** Reviews and attaches an outline supplied by the area tool, or restores one from chat. */
@Injectable()
export class RegionChatSelectionStore implements OnDestroy {
  private cropService = inject(PageRegionCropService);
  private chatAttachments = inject(ChatAttachmentService);
  private chatState = inject(ChatStateService);

  active = false;
  /** Crop rendering shape, inferred from the supplied outline. */
  shape: PageRegionShapeName = 'rectangle';
  /** Whether an image was requested for the supplied outline. */
  cropRequested = false;
  /**
   * True while the store only reviews the chat attachment of an outline drawn elsewhere, by the
   * area measurement tool (Issue #2542). The outline cannot be redrawn, moved or resized here.
   */
  get reviewOnly(): boolean {
    return this.active && this.polygon.length > 0;
  }
  phase: RegionChatPhase = 'idle';

  /** The finished selection in page space. */
  polygon: NormalizedPoint[] = [];

  cropStatus: RegionCropStatus = 'none';
  crop: PageRegionAttachment | null = null;
  cropUrl: string | null = null;
  error: string | null = null;
  /** Transient notice, e.g. after copying or attaching. */
  notice: string | null = null;

  restored: RestoredRegion | null = null;
  context: RegionChatPageContext | null = null;

  private cropGeneration = 0;

  private announcements = new Subject<string>();
  /** Messages for the panel's live region. */
  readonly announcements$ = this.announcements.asObservable();

  private activeChanges = new Subject<boolean>();
  /** Emits when the mode turns on or off, including when it ends itself (Escape, page change). */
  readonly activeChanged$ = this.activeChanges.asObservable();

  private changes = new Subject<void>();
  /** Emits whenever the state changes outside Angular's own event handling (e.g. after a timer). */
  readonly changes$ = this.changes.asObservable();

  ngOnDestroy(): void {
    this.deactivate();
  }

  // ─── mode ──────────────────────────────────────────────────────────

  /** Enters selection mode for the current page. */
  activate(): void {
    this.restored = null;
    if (this.active) return;
    this.active = true;
    this.phase = 'idle';
    this.activeChanges.next(true);
    this.announce('Review the area image before attaching it to chat. Escape returns to your areas.');
  }

  /** Leaves selection mode, releasing a crop that was never attached. */
  deactivate(announce = false): void {
    const wasActive = this.active;
    this.active = false;
    this.clearSelection(false);
    if (wasActive) {
      this.activeChanges.next(false);
    }
    if (wasActive && announce) {
      this.announce('Select area is off.');
    }
  }

  /**
   * Records the page the selection belongs to. A different page ends the mode and drops any
   * selection or restored region, so pixels from two pages are never combined.
   *
   * @returns true when the page identity changed
   */
  setPageContext(context: RegionChatPageContext | null): boolean {
    const previous = this.context;
    this.context = context;
    const samePage =
      !!previous &&
      !!context &&
      previous.projectId === context.projectId &&
      previous.fileId === context.fileId &&
      previous.pageNumber === context.pageNumber;
    if (samePage) return false;
    if (previous) {
      if (this.active) {
        this.deactivate(false);
        this.announce('Select area was closed because the page changed.');
      }
      if (this.restored && !this.restoredMatches(context)) {
        this.restored = null;
      }
    }
    return true;
  }

  /** The supplied outline on the current display. */
  get displayPolygon(): NormalizedPoint[] {
    return this.context ? polygonToDisplay(this.polygon, this.context.rotation) : this.polygon;
  }

  /** Removes the selection and releases its unattached crop. */
  clearSelection(announce = true): void {
    this.cropGeneration++;
    this.polygon = [];
    this.phase = 'idle';
    this.error = null;
    this.cropRequested = false;
    this.releaseCrop();
    if (announce) this.announce('Selection cleared.');
  }

  /**
   * Closes the image review, or hides a restored region.
   *
   * @returns whether Escape was consumed
   */
  escape(): boolean {
    if (this.active) {
      this.deactivate(false);
      return true;
    }
    if (this.restored) {
      this.restored = null;
      return true;
    }
    return false;
  }

  // ─── crop ──────────────────────────────────────────────────────────

  /** Renders the current selection on the server and shows the stored result. */
  async requestCrop(): Promise<void> {
    const context = this.context;
    if (!context || !this.polygon.length) return;
    this.cropRequested = true;
    const generation = ++this.cropGeneration;
    this.releaseCrop();
    this.cropStatus = 'generating';
    this.error = null;
    const polygon = this.polygon;
    const shape = this.shape;
    try {
      const crop = await this.cropService.createCrop({
        projectId: context.projectId,
        fileId: context.fileId,
        fileName: context.fileName,
        pageNumber: context.pageNumber,
        shape,
        polygon,
        description: context.sheetLabel,
      });
      if (generation !== this.cropGeneration) {
        // The selection changed while this crop was rendering; it no longer shows what is selected.
        void this.cropService.discard(crop.attachmentId);
        return;
      }
      // The crop is adopted only once its preview URL is in hand and it is still current. Adopting
      // it earlier would let a newer request overwrite the reference while the URL was loading,
      // leaving a staged attachment nobody can release.
      const cropUrl = await this.chatAttachments.viewUrl(crop.attachmentId);
      if (generation !== this.cropGeneration) {
        void this.cropService.discard(crop.attachmentId);
        return;
      }
      this.crop = crop;
      this.cropUrl = cropUrl;
      this.cropStatus = 'ready';
      const dpi = crop.pageRegion.renderDpi;
      this.announce(
        `Image ready: ${crop.width} by ${crop.height} pixels at ${dpi} dots per inch.${
          dpi < LOW_RESOLUTION_DPI ? ' Small text may be hard to read; select a smaller area for detail.' : ''
        }`
      );
    } catch (error) {
      if (generation !== this.cropGeneration) return;
      this.cropStatus = 'error';
      this.error =
        error instanceof PageRegionCropError ? error.message : 'The image of this area could not be prepared.';
      this.announce(this.error);
    } finally {
      this.changes.next();
    }
  }

  /** Whether the preview shows exactly the current selection and can be attached. */
  get canAttach(): boolean {
    return this.cropStatus === 'ready' && !!this.crop;
  }

  /** Whether the crop's resolution is low enough to warn about fine print. */
  get lowResolution(): boolean {
    return !!this.crop && this.crop.pageRegion.renderDpi < LOW_RESOLUTION_DPI;
  }

  /**
   * Starts the chat path for the current selection: renders the authoritative crop so the reviewer
   * can see exactly what will be sent before attaching it.
   */
  prepareForChat(): void {
    if (!this.polygon.length) return;
    if (this.cropStatus === 'ready' || this.cropStatus === 'generating') return;
    void this.requestCrop();
  }

  /**
   * Reviews the chat attachment of an outline that was drawn by the area measurement tool
   * (Issue #2542). The outline is given as fractions of the displayed page. The image is prepared
   * at once, because the reviewer has just asked to use this area in chat.
   */
  reviewOutline(displayOutline: NormalizedPoint[]): void {
    if (!this.active || !this.context || displayOutline.length < 3) return;
    // The image is outlined by shape name: "rectangle" draws the bounding box, "lasso" follows the
    // points. An oval or a polygon sent as a rectangle would be outlined as its bounding box.
    this.shape = isAxisAlignedRectangle(displayOutline) ? 'rectangle' : 'lasso';
    this.setPolygon(this.toPage(displayOutline));
    this.announce(`Area selected for chat. ${this.describeSelection()} Preparing the image.`);
    this.prepareForChat();
  }

  /** Hands the previewed crop to the chat draft and opens chat. */
  useInChat(): boolean {
    if (!this.canAttach || !this.crop) return false;
    const crop = this.crop;
    const refusal = this.chatAttachments.addReadyAttachment(crop);
    if (refusal) {
      this.error = refusal;
      this.announce(refusal);
      return false;
    }
    // The crop now belongs to the chat draft; forget it without deleting it.
    this.crop = null;
    this.cropUrl = null;
    this.cropStatus = 'none';
    this.polygon = [];
    this.phase = 'idle';
    this.cropRequested = false;
    this.cropGeneration++;
    this.chatState.openChat();
    this.notice = `${crop.displayName || crop.pageRegion.label} was attached to chat.`;
    this.announce(`${this.notice} Type your question in chat and send it.`);
    this.deactivate(false);
    return true;
  }

  /** Copies the previewed crop to the clipboard as an image. */
  async copyImage(): Promise<void> {
    if (!this.canAttach || !this.cropUrl) return;
    try {
      const response = await fetch(this.cropUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      let blob = await response.blob();
      if (blob.type !== 'image/png') {
        blob = await convertToPng(blob);
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      this.notice = 'Image copied to the clipboard.';
      this.error = null;
      this.announce(this.notice);
    } catch {
      this.error = 'The image could not be copied. Your browser may not allow copying images.';
      this.announce(this.error);
    } finally {
      this.changes.next();
    }
  }

  // ─── restored regions ──────────────────────────────────────────────

  /** Shows a region reopened from a chat source link. */
  restore(region: RestoredRegion): void {
    this.deactivate(false);
    this.restored = region;
    this.announce(
      region.stale
        ? region.message || 'This page has changed since the region was captured.'
        : `Showing the region from chat: ${region.source.label || `page ${region.source.pageNumber}`}.`
    );
  }

  /** The restored region's outline on the current display. */
  get restoredDisplayPolygon(): NormalizedPoint[] {
    if (!this.restored) return [];
    return this.context
      ? polygonToDisplay(this.restored.source.polygon, this.context.rotation)
      : this.restored.source.polygon;
  }

  // ─── helpers ───────────────────────────────────────────────────────

  /** Plain-language size and position of the selection, for screen readers. */
  describeSelection(): string {
    if (!this.polygon.length) return 'No area selected.';
    const box = boundsOf(this.displayPolygon);
    const pct = (value: number) => Math.round(value * 100);
    return `The selection is ${pct(box.width)}% of the page wide and ${pct(box.height)}% high, starting ${pct(
      box.x
    )}% from the left and ${pct(box.y)}% from the top.`;
  }

  private setPolygon(polygon: NormalizedPoint[]): void {
    this.polygon = polygon;
    this.phase = polygon.length ? 'selected' : 'idle';
    this.error = null;
    this.notice = null;
    if (this.crop || this.cropStatus === 'generating') {
      this.cropGeneration++;
      this.releaseCrop();
      this.cropStatus = 'outdated';
    } else if (polygon.length && this.cropRequested) {
      this.cropStatus = 'outdated';
    }
  }

  private toPage(display: NormalizedPoint[]): NormalizedPoint[] {
    return this.context ? polygonToPage(display, this.context.rotation) : display;
  }

  private releaseCrop(): void {
    if (this.crop) {
      void this.cropService.discard(this.crop.attachmentId);
    }
    this.crop = null;
    this.cropUrl = null;
    this.cropStatus = 'none';
  }

  private restoredMatches(context: RegionChatPageContext | null): boolean {
    const source = this.restored?.source;
    return (
      !!source &&
      !!context &&
      source.projectId === context.projectId &&
      source.fileId === context.fileId &&
      source.pageNumber === context.pageNumber
    );
  }

  private announce(message: string): void {
    this.announcements.next(message);
  }
}

/** Re-encodes an image as PNG, the one image type every browser clipboard accepts. */
async function convertToPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(result => (result ? resolve(result) : reject(new Error('PNG conversion failed'))), 'image/png')
    );
  } finally {
    // Image bitmaps hold decoded pixels outside the JavaScript heap; release them explicitly.
    bitmap.close();
  }
}
